// ============================================================================
// Avec API — Cliente HTTP
// ============================================================================
// Cliente para consumir os 230 relatórios da API Avec.
//
// Estrutura da resposta real:
//   {
//     code: 200,
//     data: {
//       report: {
//         description, requiredParams, total, result, hasMore
//       }
//     }
//   }
//
// Uso básico:
//   import { fetchReport } from './utils/avecApi';
//   const data = await fetchReport('0281', { inicio: '01/04/2026', fim: '22/04/2026' });
//   console.log(data.result);  // array com as linhas do relatório
//   console.log(data.total);   // totalizadores
//   console.log(data.hasMore); // se há mais páginas
//
// Paginação automática:
//   const all = await fetchAllPages('0002', { inicio, fim });
// ============================================================================

import { AVEC_CONFIG, isAvecConfigured } from '../config/avecConfig.js';
import { getReportById } from '../data/avecReports.js';
import {
  AVEC_RETENTATIVA, AVEC_DEDUPE_EM_VOO, AVEC_LIMITADOR, AVEC_LIMITE_PESSOAIS, AVEC_PISO_PESSOAIS, AVEC_EXIGE_REPORT,
} from '../config/featureFlags.js';
import {
  classificarFalha, esperaAntesDaTentativa, tentativasPara, criarDisjuntor, lerRetryAfter, retryAfterPassaDoTeto,
  POLITICA_RETENTATIVA, PRIORIDADE,
} from './avecRetentativa.js';
import { criarLimitador } from './limitadorConcorrencia.js';
import { ehPessoalSemCdn } from './avecClassificacao.js';

// Cabeçalho que o proxy põe com o status REAL do Avec (api/_lib/avec-flags.js).
const CABECALHO_STATUS_AVEC = 'x-avec-upstream-status';

// ─── Relógio injetável (testes sem tempo real) ─────────────────────────────
const RELOGIO_PADRAO = {
  agora: () => Date.now(),
  dormir: (ms) => new Promise(r => setTimeout(r, ms)),
  aleatorio: () => Math.random(),
  agendar: (fn, ms) => setTimeout(fn, ms),
  cancelar: (id) => clearTimeout(id),
};
const RELOGIO = { ...RELOGIO_PADRAO };
export function agoraAvec() { return RELOGIO.agora(); }

// Estado por aparelho (memória): fila, disjuntor e pedidos em voo.
let LIMITADOR = null;
let DISJUNTOR = null;
const EM_VOO = new Map();
function limitador() {
  if (!LIMITADOR) LIMITADOR = criarLimitador({ max: AVEC_LIMITE_PESSOAIS, min: AVEC_PISO_PESSOAIS, relogio: RELOGIO });
  return LIMITADOR;
}
function disjuntor() {
  if (!DISJUNTOR) DISJUNTOR = criarDisjuntor({ agora: () => RELOGIO.agora() });
  return DISJUNTOR;
}
export function reiniciarEstadoAvecApi() { LIMITADOR = null; DISJUNTOR = null; EM_VOO.clear(); }
export function __definirRelogioAvec(parcial) { Object.assign(RELOGIO, parcial || {}); reiniciarEstadoAvecApi(); }
export function __restaurarRelogioAvec() { Object.assign(RELOGIO, RELOGIO_PADRAO); reiniciarEstadoAvecApi(); }
export function estadoAvecApi() {
  return { limitador: LIMITADOR ? LIMITADOR.estado() : null, disjuntorAberto: !!DISJUNTOR?.aberto(), emVoo: EM_VOO.size };
}

// ─── Núcleo da requisição ──────────────────────────────────────────────────

function sessionHeader() {
  try {
    const st = sessionStorage.getItem('fk_session') || localStorage.getItem('fk_session');
    const t = st ? JSON.parse(st)?.sessionToken : null;
    return t ? { 'x-session-token': t } : {};
  } catch { return {}; }
}

// Pedido ao /api/avec. reqOpts (23/10/2026, todos opcionais):
//   prioridade: 0 tela atual / Tentar de novo, 1 comparação e telas comuns
//               (padrão), 2 sync de 60 s e grade diária, 3 histórico;
//   deadline:   ms absolutos (agoraAvec()) até quando vale a pena insistir.
// Com AVEC_RETENTATIVA desligada o laço é o de antes, byte a byte.
async function avecRequest(reportId, params = {}, reqOpts = {}) {
  if (!isAvecConfigured()) {
    throw new Error('Integração com o Avec desligada neste ambiente (VITE_AVEC_DESLIGADO=1).');
  }

  // Formatar ID com zero-padding (ex: "21" → "0021")
  const id = /^\d+$/.test(String(reportId))
    ? String(reportId).padStart(4, '0')
    : String(reportId);

  // Montar querystring
  const query = new URLSearchParams();
  query.set('report', id);  // proxy precisa saber qual relatório
  if (AVEC_CONFIG.salaoUnidadeId && !params.salao_unidade_id && !params.salao) {
    query.set('salao_unidade_id', AVEC_CONFIG.salaoUnidadeId);
  }
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    const key = k === 'salao' ? 'salao_unidade_id' : k;
    query.set(key, v);
  }

  // ROTA POR PROXY VERCEL — resolve CORS (api.avec.beauty bloqueia origin
  // feelking.app). O browser chama /api/avec (mesma origem), Vercel chama
  // a Avec server-to-server e devolve a resposta crua.
  const url = `/api/avec?${query.toString()}`;

  const ctx = {
    prioridade: Number.isFinite(reqOpts.prioridade) ? reqOpts.prioridade : PRIORIDADE.ANTERIOR,
    deadline: Number.isFinite(reqOpts.deadline) ? reqOpts.deadline : null,
  };
  const executar = () => (AVEC_RETENTATIVA
    ? pedidoComRetentativa(id, url, ctx)
    : avecRequestLegado(reportId, id, url));
  if (!AVEC_DEDUPE_EM_VOO) return executar();

  // Dedupe em voo: pedido idêntico (mesma URL canônica e mesma presença de
  // sessão) recebe a MESMA promessa, inclusive as novas tentativas. Quem chega
  // depois sobe a prioridade do pedido e alarga o prazo, nunca encurta.
  const canon = [...query.entries()].sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : (a[1] < b[1] ? -1 : a[1] > b[1] ? 1 : 0)))
    .map(([k, v]) => `${k}=${v}`).join('&');
  const chave = `${canon}|s${sessionHeader()['x-session-token'] ? 1 : 0}`;
  const emVoo = EM_VOO.get(chave);
  if (emVoo) {
    if (ctx.prioridade < emVoo.ctx.prioridade) {
      emVoo.ctx.prioridade = ctx.prioridade;
      if (LIMITADOR) LIMITADOR.reordenar();
    }
    emVoo.ctx.deadline = (emVoo.ctx.deadline == null || ctx.deadline == null) ? null : Math.max(emVoo.ctx.deadline, ctx.deadline);
    return emVoo.promessa;
  }
  const entrada = { ctx, promessa: null };
  entrada.promessa = (async () => {
    try { return await executar(); }
    finally { if (EM_VOO.get(chave) === entrada) EM_VOO.delete(chave); }
  })();
  EM_VOO.set(chave, entrada);
  return entrada.promessa;
}

// ─── Caminho novo (AVEC_RETENTATIVA) ───────────────────────────────────────
function erroAvec(id, falha, tentativas) {
  const motivo = falha?.motivo || 'erro';
  const msg = motivo === 'http_401'
    ? `Avec API 401 (sem autorização) no relatório ${id}`
    : `Avec API erro ${falha?.code ?? motivo} no relatório ${id} (${motivo})`;
  const e = new Error(msg);
  e.motivo = motivo;
  if (falha?.code != null) e.code = falha.code;
  e.transitoria = !!falha?.transitoria;
  e.tentativas = tentativas;
  return e;
}

async function umaTentativa(id, url, deadline) {
  const controller = new AbortController();
  const restante = deadline != null ? deadline - RELOGIO.agora() : Infinity;
  const limite = Math.max(1, Math.min(AVEC_CONFIG.timeout, restante));
  const timer = RELOGIO.agendar(() => controller.abort(), limite);
  let res;
  try {
    res = await fetch(url, {
      method: 'GET',
      headers: { 'Accept': 'application/json', ...sessionHeader() },
      cache: 'no-store',
      signal: controller.signal,
    });
  } catch (err) {
    RELOGIO.cancelar(timer);
    const f = classificarFalha({ erro: err });
    if (f.motivo === 'timeout' && restante <= AVEC_CONFIG.timeout) f.motivo = 'prazo';
    return { ok: false, falha: f };
  }
  RELOGIO.cancelar(timer);
  // O proxy repassa o HTML do nginx com Content-Type JSON: lê como texto.
  let texto = '';
  try { texto = await res.text(); } catch { texto = ''; }
  let corpo = null;
  let naoJson = false;
  try {
    corpo = texto ? JSON.parse(texto) : null;
    naoJson = !corpo || typeof corpo !== 'object';
  } catch { naoJson = true; corpo = null; }
  const cab = res.headers?.get?.(CABECALHO_STATUS_AVEC) ?? null;
  const falha = classificarFalha({ status: res.status, corpo, naoJson, cabecalhoUpstream: cab, exigeReport: AVEC_EXIGE_REPORT });
  if (falha) {
    falha.retryAfterS = lerRetryAfter(res.headers?.get?.('retry-after'));
    if (falha.motivo === 'http_401') {
      console.warn(`[avecApi] ${id} HTTP 401 — sem autorização (sessão ausente/inválida), não é "sem venda"`);
    }
    return { ok: false, falha };
  }
  if (corpo?.data?.report) {
    const t = corpo.data.report.total;
    const totalEmpty = !Array.isArray(t) || t.length === 0 || (t[0] && (t[0].valor === null || t[0].valor === undefined));
    if (totalEmpty) console.debug(`[avecApi] ${id} 200 OK mas total vazio:`, JSON.stringify(t));
  }
  const report = corpo?.data?.report || {};
  return {
    ok: true,
    valor: {
      description: report.description || '',
      requiredParams: report.requiredParams || [],
      total: report.total || null,
      result: Array.isArray(report.result) ? report.result : [],
      hasMore: Boolean(report.hasMore),
      _raw: corpo,
    },
  };
}

async function pedidoComRetentativa(id, url, ctx) {
  const pessoal = ehPessoalSemCdn(id);
  const usarFila = AVEC_LIMITADOR && pessoal;
  let timeouts = 0;
  for (let n = 1; ; n++) {
    if (ctx.deadline != null && RELOGIO.agora() >= ctx.deadline) throw erroAvec(id, { motivo: 'prazo', transitoria: true }, n - 1);
    // Disjuntor aberto: o fundo falha rápido SÓ nos relatórios sem CDN (revisão
    // 23/10/2026, achado 12). Os agregados (0281, 0326, 0041...) vêm da CDN e não
    // pesam no Avec; barrar também esses fazia a rodada de 60 s inteira cair.
    if (ctx.prioridade >= PRIORIDADE.FUNDO && pessoal && disjuntor().aberto()) throw erroAvec(id, { motivo: 'disjuntor', transitoria: true }, n - 1);
    let r;
    try {
      r = usarFila
        ? await limitador().executar(() => ctx.prioridade, () => umaTentativa(id, url, ctx.deadline), { deadline: ctx.deadline })
        : await umaTentativa(id, url, ctx.deadline);
    } catch (e) {
      // a fila recusou (prazo ou disjuntor) sem chegar à rede
      if (e?.motivo) throw erroAvec(id, { motivo: e.motivo, transitoria: true }, n - 1);
      throw e;
    }
    if (r.ok) {
      if (usarFila) limitador().sucesso();
      return r.valor;
    }
    const f = r.falha;
    if (disjuntor().registrar(f.motivo) && AVEC_LIMITADOR) {
      // disjuntor abriu: pausa a fila e o fundo (prioridade >= 2) falha rápido
      limitador().pausar(5000);
      limitador().rejeitarFila(it => it.prioridade >= PRIORIDADE.FUNDO, 'disjuntor');
    }
    if (usarFila && (f.motivo === 'http_403_avec' || f.motivo === 'http_429')) {
      limitador().falhaDeTaxa();
      limitador().pausar(1500 + Math.floor(RELOGIO.aleatorio() * 1000));
    }
    if (f.motivo === 'timeout') timeouts++;
    const max = tentativasPara(ctx.prioridade, { disjuntorAberto: disjuntor().aberto() || disjuntor().foraDoAr() });
    // Retry-After acima do teto: o servidor pediu silêncio por mais tempo do que
    // vale esperar aqui; falha agora e a nova tentativa fica para o "Tentar de
    // novo" ou para a próxima rodada (achado 22).
    const repete = f.transitoria && n < max && !(f.motivo === 'timeout' && timeouts >= POLITICA_RETENTATIVA.maxTimeouts)
      && !retryAfterPassaDoTeto(f.retryAfterS);
    if (!repete) throw erroAvec(id, f, n);
    const espera = esperaAntesDaTentativa(n + 1, { retryAfterS: f.retryAfterS, aleatorio: RELOGIO.aleatorio });
    if (ctx.deadline != null && RELOGIO.agora() + espera >= ctx.deadline) throw erroAvec(id, f, n);
    console.warn(`[avecApi] ${id} ${f.motivo}: nova tentativa ${n + 1}/${max}`);
    await RELOGIO.dormir(espera);
  }
}

// ─── Caminho de antes (AVEC_RETENTATIVA desligada): o laço original ────────
async function avecRequestLegado(reportId, id, url) {
  // Retry com backoff exponencial para lidar com rate limit (429/503/timeout)
  const maxRetries = 2;
  let attempt = 0;
  let lastErr = null;
  while (attempt <= maxRetries) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), AVEC_CONFIG.timeout);
    try {
      const res = await fetch(url, {
        method: 'GET',
        // x-session-token: relatórios com PII de cliente agora exigem sessão
        // staff no proxy (MVP 0 do DNA 360, 02/09/2026)
        headers: { 'Accept': 'application/json', ...sessionHeader() },
        cache: 'no-store',
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      const body = await res.json().catch(() => ({}));

      // Retry em 429 / 503
      if ((res.status === 429 || res.status === 503) && attempt < maxRetries) {
        attempt++;
        console.warn(`[avecApi] ${reportId} HTTP ${res.status} — retry ${attempt}/${maxRetries}`);
        await new Promise(r => setTimeout(r, 500 * Math.pow(2, attempt)));
        continue;
      }

      // 401 do proxy = SEM AUTORIZAÇÃO (sessão ausente/inválida em relatório
      // PII) — identidade própria pro consumidor não confundir com "sem venda".
      // Sem retry: repetir sem sessão devolve 401 de novo.
      if (res.status === 401) {
        console.warn(`[avecApi] ${id} HTTP 401 — sem autorização (sessão ausente/inválida), não é "sem venda"`);
        const authErr = new Error(`Avec API 401 (sem autorização) no relatório ${id}`);
        authErr.code = 401;
        throw authErr;
      }

      // Sucesso mas com total vazio — log pra debug
      if (res.ok && body?.data?.report) {
        const t = body.data.report.total;
        const totalEmpty = !Array.isArray(t) || t.length === 0 || (t[0] && (t[0].valor === null || t[0].valor === undefined));
        if (totalEmpty) {
          console.debug(`[avecApi] ${reportId} 200 OK mas total vazio:`, JSON.stringify(t));
        }
      }

      // Erro de negócio ou HTTP
      if (!res.ok || body.code >= 400) {
        const msg = body?.data?.validation?.join('; ')
          || body?.data?.validations?.join('; ')
          || body?.message
          || res.statusText;
        throw new Error(`Avec API erro ${body.code || res.status}: ${msg}`);
      }

      // Sucesso — desempacota {code, data: {report: {...}}}
      const report = body?.data?.report || {};
      return {
        description: report.description || '',
        requiredParams: report.requiredParams || [],
        total: report.total || null,
        result: Array.isArray(report.result) ? report.result : [],
        hasMore: Boolean(report.hasMore),
        _raw: body,
      };
    } catch (err) {
      clearTimeout(timeoutId);
      lastErr = err;
      // Timeout/abort — tenta de novo se tem retries
      if (err.name === 'AbortError' && attempt < maxRetries) {
        attempt++;
        await new Promise(r => setTimeout(r, 500 * Math.pow(2, attempt)));
        continue;
      }
      if (err.name === 'AbortError') throw new Error(`Avec API timeout (${AVEC_CONFIG.timeout}ms)`);
      throw err;
    }
  }
  throw lastErr || new Error('Avec API: retries esgotados');
}

// ─── API pública ───────────────────────────────────────────────────────────

/**
 * Busca um relatório pelo ID (ex: '0281', '0002', '0021')
 * Aceita tanto string '0281' quanto number 281 (auto-formata com zero padding).
 *
 * @param {string|number} reportId - ID do relatório
 * @param {object} params - Parâmetros (inicio, fim, page, limit, etc)
 * @returns {Promise<{description, requiredParams, total, result, hasMore}>}
 */
export async function fetchReport(reportId, params = {}, reqOpts = {}) {
  const defaults = { page: 1, limit: AVEC_CONFIG.maxPageSize };
  return avecRequest(reportId, { ...defaults, ...params }, reqOpts);
}

/**
 * Alias para compatibilidade (antes era fetchReportById)
 */
export const fetchReportById = fetchReport;

/**
 * Busca TODAS as páginas de um relatório automaticamente
 * @param {string|number} reportId - ID do relatório
 * @param {object} params - Parâmetros (page será sobrescrito)
 * @param {object} opts - { onProgress, maxPages }
 * @returns {Promise<Array>} Array com todas as linhas concatenadas
 */
export async function fetchAllPages(reportId, params = {}, opts = {}) {
  const { onProgress, maxPages = 100 } = opts;
  const reqOpts = {};
  if (opts.prioridade != null) reqOpts.prioridade = opts.prioridade;
  if (opts.deadline != null) reqOpts.deadline = opts.deadline;
  const results = [];
  let page = 1;
  let hasMore = true;

  while (hasMore && page <= maxPages) {
    const data = await fetchReport(reportId, { ...params, page }, reqOpts);
    results.push(...(data.result || []));
    hasMore = data.hasMore;
    if (onProgress) onProgress(page, hasMore, data.result?.length || 0);
    page++;
  }

  return results;
}

/**
 * Testa a conexão com a API Avec
 * Usa relatório 0229 (Lista de Profissionais) que não requer parâmetros.
 * @returns {Promise<{ok, error?, user?, sample?}>}
 */
export async function testAvecConnection() {
  try {
    const data = await fetchReport('0229', { page: 1, limit: 5 });
    const first = data.result[0] || {};
    return {
      ok: true,
      description: data.description,
      totalResult: data.result.length,
      hasMore: data.hasMore,
      sampleProfessional: first.nome || null,
      sampleRole: first.cargo || null,
    };
  } catch (err) {
    return { ok: false, error: err.message };
  }
}

// ─── Helpers de datas ──────────────────────────────────────────────────────

/**
 * Formata Date → formato Avec: DD/MM/YYYY
 */
export function formatAvecDate(date) {
  const d = date instanceof Date ? date : new Date(date);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

/** Primeiro dia do mês corrente (formato Avec) */
export function firstDayOfCurrentMonth() {
  const d = new Date();
  return formatAvecDate(new Date(d.getFullYear(), d.getMonth(), 1));
}

/** Último dia do mês corrente */
export function lastDayOfCurrentMonth() {
  const d = new Date();
  return formatAvecDate(new Date(d.getFullYear(), d.getMonth() + 1, 0));
}

/** Hoje (formato Avec) */
export function todayAvec() {
  return formatAvecDate(new Date());
}

/** Primeiro e último dia de um mês específico: getMonthRange(2026, 3) */
export function getMonthRange(year, month /* 1-12 */) {
  const inicio = formatAvecDate(new Date(year, month - 1, 1));
  const fim = formatAvecDate(new Date(year, month, 0));
  return { inicio, fim };
}

// ─── Export default ────────────────────────────────────────────────────────
export default {
  fetchReport,
  fetchReportById,
  fetchAllPages,
  testAvecConnection,
  formatAvecDate,
  firstDayOfCurrentMonth,
  lastDayOfCurrentMonth,
  todayAvec,
  getMonthRange,
};
